import { GitBranch01 } from "@untitledui/icons";
import { Badge } from "@/components/base/badges/badges";
import { FeaturedIcon } from "@/components/foundations/featured-icon/featured-icon";
import { AccordionItem } from "@/components/Molecules/AccordionItem/AccordionItem";
import { LifecycleFlowVisualization } from "@/components/Organisms/LifecycleFlowVisualization/LifecycleFlowVisualization";
import { useAppTranslation } from "@/hooks/use-app-translation";
import type { StatusLifecycleDisplay } from "@/services/lifecycle/lifecycle.types";

export interface EntityTypeLifecyclePanelProps {
	name: string;
	description: string | null | undefined;
	lifecycle: StatusLifecycleDisplay | null;
	hasLifecycle: boolean;
	/** Null hides the badge until System or Custom is known. */
	isSystem: boolean | null;
	isLoading?: boolean;
	emptyTitle: string;
	emptyDescription: string;
	errorMessage: string;
}

export function EntityTypeLifecyclePanel({
	name,
	description,
	lifecycle,
	hasLifecycle,
	isSystem,
	isLoading = false,
	emptyTitle,
	emptyDescription,
	errorMessage,
}: EntityTypeLifecyclePanelProps) {
	const { t } = useAppTranslation();

	if (!hasLifecycle) {
		return (
			<div className="rounded-xl border border-primary bg-primary px-5 py-4">
				<p className="text-sm font-bold text-primary">{emptyTitle}</p>
				<p className="text-xs font-medium text-secondary">{emptyDescription}</p>
			</div>
		);
	}

	return (
		<AccordionItem
			size="sm"
			primaryText={name}
			secondaryText={description || undefined}
			showSecondaryText={Boolean(description)}
			showLeadingSlot
			leadingSlot={
				<FeaturedIcon
					icon={GitBranch01}
					size="sm"
					color="brand"
					theme="light"
				/>
			}
			showBadge={isSystem !== null}
			badge={
				isSystem === null ? undefined : (
					<Badge type="pill-color" color="gray" size="sm">
						{isSystem
							? t("admin.jobTypes.drawer.details.source.system")
							: t("admin.jobTypes.drawer.details.source.custom")}
					</Badge>
				)
			}
			defaultExpanded
		>
			{isLoading ? (
				<p className="py-8 text-center text-sm text-tertiary">
					{t("common.actions.loadingEllipsis")}
				</p>
			) : lifecycle ? (
				<LifecycleFlowVisualization lifecycle={lifecycle} height={420} />
			) : (
				<p className="py-8 text-center text-sm text-tertiary">{errorMessage}</p>
			)}
		</AccordionItem>
	);
}
